import { Lang } from "@/lib/i18n";
import { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "PlainDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Dutch Weave Filter Cloth",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Plain Dutch Weave Wire Mesh",
      lead: "Engineered with heavy, widely spaced warp wires and tightly beaten fine shute wires, Plain Dutch Weave wire cloth produces uniform triangular pore openings for dependable particulate retention from 20 µm to 310 µm with smooth surface cake release.",
      secondaryCta: {
        label: "View All Filter Cloth",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Plain Dutch Weave stainless steel wire cloth macro view",
      },
      cardNote: "1/1 plain weave micro-pore media",
      cardLink: {
        label: "Specifications",
        to: "/PlainDutchWeavePage#specifications",
      },
      specs: [
        { label: "Mesh Range", value: "8×85 – 80×400 (to 165×1200)" },
        { label: "Retention Rating", value: "~20 – 310 µm nominal" },
        { label: "Weave Structure", value: "1/1 Plain Dutch interlacing" },
        { label: "Standard Alloys", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Triangular Micronic Geometry",
      heading: "Predictable Retention and Clean Cake Discharge",
      paragraphs: [
        "In Plain Dutch Weave (also referred to as plain dutch wire cloth or glatte tresse), the warp wires remain straight under high tension, while smaller diameter weft wires are driven tightly against one another in a classic one-over / one-under plain interlacing motion.",
        "Because the fine weft wires touch each other continuously along the length of the fabric, straight-through optical light is completely obstructed. Liquid or gas traversing the mesh must travel through wedge-shaped triangular apertures defined by the tangential contact of adjacent wires.",
        "This configuration provides a firm, rigid surface profile that resists blinding and allows swift cake dislodgement during backflushing or scraper discharge. MutualFilter supplies plain dutch weave cloth in custom slit coils, circular filter discs, cylindrical tube cartridges, and fabricated pressure leaves.",
      ],
      keyPoints: [
        "Heavy warp wires paired with compacted fine shute wires produce uniform triangular pore geometry for predictable particulate retention and high cake discharge.",
        "Zero straight-through apertures prevent particulate bleed-through even under elevated hydraulic cycle pressure.",
        "Standard manufacturing mesh range from 8×85 to 80×400, with high-flow variants available up to 165×1200.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Stainless steel plain dutch weave cloth surface and texture",
      },
    },
    specTable: {
      heading: "Plain Dutch Weave Standard Specifications",
      intro: "Representative manufacturing specifications for stainless steel plain dutch weave wire cloth conforming to ASTM E2814 and ISO 9044.",
      columns: [
        { key: "mesh", label: "Mesh (Warp × Weft)", align: "left" },
        { key: "warpWire", label: "Warp Wire (mm)", align: "right" },
        { key: "weftWire", label: "Weft Wire (mm)", align: "right" },
        { key: "retention", label: "Nominal Micron Rating (µm)", align: "right" },
        { key: "standard", label: "Compliance Standard", align: "right" },
      ],
      rows: [
        {
          id: "row-pd-1",
          mesh: "12 × 64",
          warpWire: "0.58",
          weftWire: "0.40",
          retention: "280 – 310",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-2",
          mesh: "24 × 110",
          warpWire: "0.35",
          weftWire: "0.25",
          retention: "115 – 125",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-3",
          mesh: "30 × 150",
          warpWire: "0.25",
          weftWire: "0.18",
          retention: "85 – 95",
          standard: "ISO 9044",
        },
        {
          id: "row-pd-4",
          mesh: "50 × 250",
          warpWire: "0.14",
          weftWire: "0.11",
          retention: "50 – 60",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-5",
          mesh: "80 × 400",
          warpWire: "0.13",
          weftWire: "0.07",
          retention: "25 – 35",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-6",
          mesh: "165 × 1200",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "20 – 25",
          standard: "ASTM E2814",
        },
      ],
      caption: "Nominal retention ranges derived from glass bead challenge and bubble point test correlations per ISO 9044.",
    },
    features: {
      heading: "Design and Operational Benefits",
      intro: "Structural advantages of plain dutch weave wire cloth in process filtration applications.",
      items: [
        {
          title: "Rigid Warp Core",
          text: "Heavy longitudinal wires support mechanical stresses across spans, reducing the necessity for heavy auxiliary backup meshes.",
        },
        {
          title: "Surface-Filtration Dynamics",
          text: "Particulates are captured predominantly at the cloth boundary surface, preventing deep pore entrainment and accelerating cake removal.",
        },
        {
          title: "High Mechanical Stability",
          text: "Tight weft beat-up produces high wire-to-wire friction that locks wire positions and resists pore shifting under vibrating or pulsing flow.",
        },
        {
          title: "Chemical Cleanability",
          text: "Corrosion-resistant AISI 304, 304L, 316, and 316L alloys permit repeated aggressive chemical clean-in-place (CIP) and thermal baking cycles.",
        },
      ],
    },
    related: {
      heading: "Explore Related Filter Cloth Variants",
      intro: "Compare Plain Dutch Weave with alternative weave constructions across the Dutch weave family.",
      cards: [
        {
          title: "Twill Dutch Weave",
          text: "Twilled warp wire arrangement allows double the weft density with zero direct line of sight, delivering micro-retention down to 1 micron under severe differential pressures.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Twill Dutch Weave wire cloth",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "View Twill Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Dense warp wires paired with heavier spaced weft wires deliver exceptional longitudinal tensile strength for continuous screen belts and high-pressure polymer melt filtration.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Reverse Dutch Weave wire cloth",
          },
          badge: "72×15 – 325×38",
          linkLabel: "View Reverse Dutch",
        },
        {
          title: "Filter Cloth Overview",
          text: "Comprehensive family overview detailing Dutch weave engineering characteristics, alloy selections, and fabrication options.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Filter cloth overview",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "View Parent Page",
        },
      ],
    },
    cta: {
      id: "plain-dutch-cta",
      eyebrow: "Custom Plain Dutch Media",
      heading: "Order Precision Plain Dutch Weave Wire Mesh",
      lead: "MutualFilter provides precision rolls, slit ribbons, and cut-to-shape discs fabricated to your exact project parameters.",
      secondaryLabel: "Explore Filter Cloth",
      secondaryTo: "/FilterClothPage",
      note: "Material test reports and compliance certificates available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "PlainDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "荷兰编织工业滤布",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "平纹荷兰编织丝网",
      lead: "采用粗直径大间距经丝与高密度密排细纬丝编织而成，平纹荷兰编织（平纹密纹网）形成高度规则的三角形立体微孔，公称截留精度覆盖 20 µm 至 310 µm，兼具优异的颗粒拦截与滤饼脱离性能。",
      secondaryCta: {
        label: "查看滤布总览",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "不锈钢平纹荷兰编织滤布微距拍摄",
      },
      cardNote: "1/1 平纹密纹微孔介质",
      cardLink: {
        label: "技术参数表",
        to: "/PlainDutchWeavePage#specifications",
      },
      specs: [
        { label: "目数范围", value: "8×85 – 80×400 (最高至 165×1200)" },
        { label: "公称精度", value: "~20 – 310 µm nominal" },
        { label: "编织形式", value: "1/1 平纹荷兰密排交织" },
        { label: "标准材质", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "规则微米级三角微孔",
      heading: "稳定的颗粒拦截与出色的滤饼剥离特性",
      paragraphs: [
        "平纹荷兰编织网（俗称平纹密纹网或平纹席型网）中，较粗的经丝在高张力下保持平直拉伸，而较细的纬丝则通过经典的一上一平纹走线紧密打纬、相互贴合。",
        "由于细纬丝在纵向相互紧贴接触，网面完全遮挡了直射光线。穿透滤网的液体或气体必须通过相邻金属丝切线接触所构成的楔形三角微孔通道。",
        "这一几何构造形成了平整致密的表面轮廓，不仅能够有效阻止杂质深层嵌入，还能在脉冲气反吹、液体反洗或机械刮刀清除时实现滤饼迅速剥落。MutualFilter 提供定宽分切卷材、圆形冲压滤片及深加工滤筒。",
      ],
      keyPoints: [
        "粗经丝配合紧密敲紧的细纬丝形成规则三角微孔流道，兼具稳定的固体颗粒拦截精度与良好的表面滤饼剥离特性。",
        "无直射透光微孔通道，有效避免高压差工况下细小杂质直通泄漏。",
        "常规目数覆盖 8×85 至 80×400，高流速规格可达 165×1200。",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "不锈钢平纹密纹网表面微观细节",
      },
    },
    specTable: {
      heading: "平纹荷兰编织常用技术规格表",
      intro: "符合 ASTM E2814 及 ISO 9044 标准的不锈钢平纹荷兰编织标准制造规格代表数据。",
      columns: [
        { key: "mesh", label: "目数 (经 × 纬)", align: "left" },
        { key: "warpWire", label: "经丝丝径 (mm)", align: "right" },
        { key: "weftWire", label: "纬丝丝径 (mm)", align: "right" },
        { key: "retention", label: "公称精度 (µm)", align: "right" },
        { key: "standard", label: "执行标准", align: "right" },
      ],
      rows: [
        {
          id: "row-pd-1",
          mesh: "12 × 64",
          warpWire: "0.58",
          weftWire: "0.40",
          retention: "280 – 310",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-2",
          mesh: "24 × 110",
          warpWire: "0.35",
          weftWire: "0.25",
          retention: "115 – 125",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-3",
          mesh: "30 × 150",
          warpWire: "0.25",
          weftWire: "0.18",
          retention: "85 – 95",
          standard: "ISO 9044",
        },
        {
          id: "row-pd-4",
          mesh: "50 × 250",
          warpWire: "0.14",
          weftWire: "0.11",
          retention: "50 – 60",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-5",
          mesh: "80 × 400",
          warpWire: "0.13",
          weftWire: "0.07",
          retention: "25 – 35",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-6",
          mesh: "165 × 1200",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "20 – 25",
          standard: "ASTM E2814",
        },
      ],
      caption: "公称截留精度符合 ISO 9044 玻璃微珠与气泡点对比试验标准。",
    },
    features: {
      heading: "结构与运行技术优势",
      intro: "平纹荷兰编织丝网在工业固液分离应用中的结构优势。",
      items: [
        {
          title: "坚固的经向支撑骨架",
          text: "粗径经丝提供了极高的抗拉与抗弯曲刚度，减少了大跨度滤板对重型背衬网的依赖。",
        },
        {
          title: "表面截留工作机制",
          text: "杂质颗粒绝大部分被截留在网面最外层，不易在孔隙深处淤积，极大地提高了清洗效率。",
        },
        {
          title: "紧密稳定的机械锁合",
          text: "高密实度的密排打纬产生强烈的金属线间摩擦力，避免振动或流体脉冲引发网孔位移变形。",
        },
        {
          title: "优良耐化学清洗",
          text: "精选 AISI 304、304L、316 及 316L 不锈钢材料，耐受酸碱 CIP 在线清洗与高温灭菌。",
        },
      ],
    },
    related: {
      heading: "相关荷兰编织产品系列",
      intro: "对比平纹荷兰编织与同系列其它编织结构的技术特性。",
      cards: [
        {
          title: "斜纹荷兰编织",
          text: "结合斜纹走线与双向压紧的极高纬丝密度，形成无直射光透孔的曲折立体通道，在高压差恶劣工况下稳定实现低至 1 微米精密截留。",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "斜纹荷兰编织工业滤布",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "查看斜纹荷兰编织",
        },
        {
          title: "反向荷兰编织",
          text: "高密度紧排细经丝跨越较粗的间距纬丝，提供出色的纵向抗拉强度，专为自动换网器连续过滤带与高温高压聚合物熔体挤出设计。",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "反向荷兰编织工业滤布",
          },
          badge: "72×15 – 325×38",
          linkLabel: "查看反向荷兰编织",
        },
        {
          title: "工业滤布总览",
          text: "查看荷兰编织工业滤布家族整体技术对比、材质选择指南及深加工形态。",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "工业滤布产品总览",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "返回上级页面",
        },
      ],
    },
    cta: {
      id: "plain-dutch-cta",
      eyebrow: "定制平纹密纹网",
      heading: "获取不锈钢平纹荷兰编织丝网技术报价",
      lead: "MutualFilter 提供工业卷材、精密分切窄带及定制冲压圆片等多种供货方案。",
      secondaryLabel: "返回工业滤布总览",
      secondaryTo: "/FilterClothPage",
      note: "材质检验报告可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "PlainDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tela Filtrante Dutch",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Malla de Tejido Dutch Liso",
      lead: "Fabricada con alambres de urdimbre gruesos espaciados y alambres de trama finos compactados, el tejido Dutch liso produce aberturas triangulares uniformes para una retención confiable de 20 µm a 310 µm y fácil descarga de torta.",
      secondaryCta: {
        label: "Ver Tela Filtrante",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Malla de tejido Dutch liso en acero inoxidable",
      },
      cardNote: "Ligamento liso 1/1 de microporos",
      cardLink: {
        label: "Especificaciones",
        to: "/PlainDutchWeavePage#specifications",
      },
      specs: [
        { label: "Rango de Malla", value: "8×85 – 80×400 (hasta 165×1200)" },
        { label: "Retención Nominal", value: "~20 – 310 µm nominal" },
        { label: "Estructura", value: "Entrelazado liso Dutch 1/1" },
        { label: "Aleaciones", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Geometría Micrométrica Triangular",
      heading: "Retención Predecible y Descarga Rápida de Torta",
      paragraphs: [
        "En el tejido Dutch liso (plain dutch wire cloth), los alambres de urdimbre permanecen rectos bajo tensión, mientras que los alambres finos de trama se baten de manera apretada en un movimiento de ligamento liso 1 sobre / 1 debajo.",
        "Dado que los alambres de trama hacen contacto continuo, se elimina el paso recto de luz. El fluido a filtrar debe circular por canales triangulares micrométricos formados entre alambres adyacentes.",
        "Esta configuración aporta una superficie rígida y resistente a la colmatación, facilitando la separación de la torta durante el retrolavado. MutualFilter suministra rollos, cintas cortadas y discos estampados.",
      ],
      keyPoints: [
        "Hilos de urdimbre gruesos con alambres de trama fina compactada forman poros triangulares uniformes para retención predecible y descarga de torta.",
        "Cero paso recto directo para evitar escapes de partículas bajo presiones pulsantes.",
        "Gama estándar de 8×85 a 80×400, con opciones de alto flujo hasta 165×1200.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Detalle superficial del tejido Dutch liso",
      },
    },
    specTable: {
      heading: "Especificaciones Estándar de Tejido Dutch Liso",
      intro: "Especificaciones representativas para tela metálica de tejido Dutch liso conforme a ASTM E2814 e ISO 9044.",
      columns: [
        { key: "mesh", label: "Malla (Urdimbre × Trama)", align: "left" },
        { key: "warpWire", label: "Hilo Urdimbre (mm)", align: "right" },
        { key: "weftWire", label: "Hilo Trama (mm)", align: "right" },
        { key: "retention", label: "Retención Nominal (µm)", align: "right" },
        { key: "standard", label: "Norma", align: "right" },
      ],
      rows: [
        {
          id: "row-pd-1",
          mesh: "12 × 64",
          warpWire: "0.58",
          weftWire: "0.40",
          retention: "280 – 310",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-2",
          mesh: "24 × 110",
          warpWire: "0.35",
          weftWire: "0.25",
          retention: "115 – 125",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-3",
          mesh: "30 × 150",
          warpWire: "0.25",
          weftWire: "0.18",
          retention: "85 – 95",
          standard: "ISO 9044",
        },
        {
          id: "row-pd-4",
          mesh: "50 × 250",
          warpWire: "0.14",
          weftWire: "0.11",
          retention: "50 – 60",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-5",
          mesh: "80 × 400",
          warpWire: "0.13",
          weftWire: "0.07",
          retention: "25 – 35",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-6",
          mesh: "165 × 1200",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "20 – 25",
          standard: "ASTM E2814",
        },
      ],
      caption: "Rangos de retención nominal según ensayos de punto de burbuja conformes a ISO 9044.",
    },
    features: {
      heading: "Ventajas de Rendimiento",
      intro: "Beneficios estructurales del tejido Dutch liso en procesos industriales.",
      items: [
        {
          title: "Soporte Longitudinal Robusto",
          text: "Los alambres de urdimbre pesados soportan cargas mecánicas elevadas en bastidores de filtración.",
        },
        {
          title: "Filtración Superficial Eficiente",
          text: "Las partículas quedan retenidas en la superficie del tejido sin penetrar profundamente en los poros.",
        },
        {
          title: "Estabilidad de Malla",
          text: "El empaquetamiento apretado de la trama fija los alambres evitando desplazamientos bajo flujo.",
        },
        {
          title: "Compatibilidad Química",
          text: "Alambres de acero inoxidable AISI 304, 304L, 316 y 316L resistentes a ciclos CIP frecuentes.",
        },
      ],
    },
    related: {
      heading: "Mallas Filtrantes Relacionadas",
      intro: "Explore otras construcciones dentro de la familia de telas filtrantes Dutch.",
      cards: [
        {
          title: "Twill Dutch Weave",
          text: "La disposición en sarga duplica la densidad de trama sin paso de luz directo, ofreciendo micro-retención de hasta 1 micra bajo severas presiones diferenciales.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Malla Twill Dutch Weave",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Ver Twill Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Urdidumbre de alta densidad combinada con alambres de trama más gruesos ofrece gran resistencia a la tracción para cambiadores de pantalla continuos y extrusión.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Malla Reverse Dutch Weave",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Ver Reverse Dutch",
        },
        {
          title: "Resumen de Tela Filtrante",
          text: "Visión global de las opciones de tejido Dutch, aleaciones y métodos de confección industrial.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Resumen de telas filtrantes",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Página Principal",
        },
      ],
    },
    cta: {
      id: "plain-dutch-cta",
      eyebrow: "Tela Metálica a Medida",
      heading: "Solicite Cotización de Malla Dutch Lisa",
      lead: "Suministramos rollos industriales, cintas cortadas y discos filtrantes terminados según plano.",
      secondaryLabel: "Ver Tela Filtrante",
      secondaryTo: "/FilterClothPage",
      note: "Informes de prueba de material disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "PlainDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tressengewebe",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Glatte Tresse (Plain Dutch Weave)",
      lead: "Ausgestattet mit starken, beabstandeten Kettdrähten und dicht geschlagenen feinen Schussdrähten bietet die glatte Tresse dreieckige Porenöffnungen für zuverlässige Partikelrückhaltung von 20 µm bis 310 µm und leichten Filterkuchenabwurf.",
      secondaryCta: {
        label: "Filtertuch-Übersicht",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Edelstahl Glatte Tresse Gewebeoberfläche",
      },
      cardNote: "1/1 Bindung Mikroporen-Medium",
      cardLink: {
        label: "Spezifikationen",
        to: "/PlainDutchWeavePage#specifications",
      },
      specs: [
        { label: "Maschenbereich", value: "8×85 – 80×400 (bis 165×1200)" },
        { label: "Filterfeinheit", value: "~20 – 310 µm nominal" },
        { label: "Bindungsart", value: "1/1 Glatte Tresse" },
        { label: "Legierungen", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Dreieckige Porengeometrie",
      heading: "Berechenbare Trennschärfe und saubere Abreinigung",
      paragraphs: [
        "Bei der glatten Tresse (Plain Dutch Weave) verlaufen die kräftigen Kettdrähte gestreckt unter hoher Spannung, während die feineren Schussdrähte dicht aneinander im 1/1-Verfahren verwebt werden.",
        "Weil die Schussdrähte lückenlos aneinanderliegen, existieren keine geradlinigen Durchgangsöffnungen. Das Fluid strömt durch dreieckige Kanäle, die an den Berührungspunkten der Drähte entstehen.",
        "Diese Gewebestruktur bietet eine formstabile Oberfläche, die Kuchenbildung unterstützt und bei der Rückspülung eine zügige Ablösung ermöglicht. MutualFilter fertigt Rollenware, Schmalbänder und gestanzte Scheiben.",
      ],
      keyPoints: [
        "Starke Kettendrähte und dicht geschlagene feine Schussdrähte bilden eine einheitliche dreieckige Porengeometrie für zuverlässigen Rückhalt und leichten Kuchenabwurf.",
        "Keine geraden Durchgangsöffnungen verhindern Partikeldurchtritt auch bei schwankendem Systemdruck.",
        "Standard-Maschenbereich von 8×85 bis 80×400, Hochfluss-Varianten bis 165×1200 lieferbar.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Nahansicht glatte Tresse aus Edelstahl",
      },
    },
    specTable: {
      heading: "Standardspezifikationen Glatte Tresse",
      intro: "Repräsentative Fertigungsdaten für Edelstahl-Tressengewebe nach ASTM E2814 und ISO 9044.",
      columns: [
        { key: "mesh", label: "Maschen (Kette × Schuss)", align: "left" },
        { key: "warpWire", label: "Kett-Draht (mm)", align: "right" },
        { key: "weftWire", label: "Schuss-Draht (mm)", align: "right" },
        { key: "retention", label: "Filterfeinheit (µm)", align: "right" },
        { key: "standard", label: "Norm", align: "right" },
      ],
      rows: [
        {
          id: "row-pd-1",
          mesh: "12 × 64",
          warpWire: "0.58",
          weftWire: "0.40",
          retention: "280 – 310",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-2",
          mesh: "24 × 110",
          warpWire: "0.35",
          weftWire: "0.25",
          retention: "115 – 125",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-3",
          mesh: "30 × 150",
          warpWire: "0.25",
          weftWire: "0.18",
          retention: "85 – 95",
          standard: "ISO 9044",
        },
        {
          id: "row-pd-4",
          mesh: "50 × 250",
          warpWire: "0.14",
          weftWire: "0.11",
          retention: "50 – 60",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-5",
          mesh: "80 × 400",
          warpWire: "0.13",
          weftWire: "0.07",
          retention: "25 – 35",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-6",
          mesh: "165 × 1200",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "20 – 25",
          standard: "ASTM E2814",
        },
      ],
      caption: "Nominale Trenngrenzen basierend auf Glasperlen- und Bubble-Point-Prüfungen nach ISO 9044.",
    },
    features: {
      heading: "Eigenschaften und Praxisvorteile",
      intro: "Wesentliche mechanische Vorzüge der glatten Tresse in industriellen Prozessen.",
      items: [
        {
          title: "Stabiler Kettaufbau",
          text: "Starke Längsdrähte gewährleisten Festigkeit auch über größere Spannweiten hinweg.",
        },
        {
          title: "Oberflächenfiltration",
          text: "Partikel verbleiben primär an der Gewebeoberfläche und dringen nicht tief in den Verbund ein.",
        },
        {
          title: "Verschiebefestigkeit",
          text: "Die dichte Schusslage erzeugt hohe Drahtreibung, die Porenverzerrungen bei Vibration verhindert.",
        },
        {
          title: "Chemische Resistenz",
          text: "Legierungen AISI 304, 304L, 316 und 316L eignen sich für anspruchsvolle CIP-Reinigungsroutinen.",
        },
      ],
    },
    related: {
      heading: "Verwandte Gewebetypen",
      intro: "Vergleichen Sie die glatte Tresse mit weiteren Ausführungen der Tressenfamilie.",
      cards: [
        {
          title: "Twill Dutch Weave",
          text: "Die köperbindige Kettanordnung verdoppelt die Schussdichte ohne Direktdurchsicht und bietet Feinstfiltration ab 1 Mikrometer unter hohem Differenzdruck.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Köpertresse Filtertuch",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Zur Köpertresse",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Dichte Kettendrähte gepaart mit stärkeren Schussdrähten bieten extreme Längszugfestigkeit für kontinuierliche Siebbänder und Hochdruck-Polymerschmelzefiltration.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Panzertresse Filtertuch",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Zur Panzertresse",
        },
        {
          title: "Filtertuch-Übersicht",
          text: "Umfassende Übersicht über Tressengewebe, Legierungsauswahl und Konfektionsmöglichkeiten.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Filtertuch Übersicht",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Übergeordnete Seite",
        },
      ],
    },
    cta: {
      id: "plain-dutch-cta",
      eyebrow: "Maßgeschneiderte Tressengewebe",
      heading: "Glatte Tresse für Ihre Filtrationsaufgabe",
      lead: "MutualFilter liefert Qualitäts-Tressengewebe als Rollen, Bänder und Stanzteile nach Spezifikation.",
      secondaryLabel: "Filtertuch-Übersicht",
      secondaryTo: "/FilterClothPage",
      note: "Werksprüfzeugnisse auf Anfrage bei Auftragserteilung erhältlich.",
    },
  },
  fr: {
    breadcrumbKey: "PlainDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Toile Métallique Reps",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Toile Tissée Reps Uni (Plain Dutch)",
      lead: "Composée de gros fils de chaîne espacés et de fils de trame fins fortement serrés, la toile reps uni produit des ouvertures triangulaires régulières pour une rétention de 20 µm à 310 µm et un démoulage optimal du gâteau de filtration.",
      secondaryCta: {
        label: "Voir Toutes les Toiles",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Toile reps uni en acier inoxydable",
      },
      cardNote: "Armure unie 1/1 à micropores",
      cardLink: {
        label: "Spécifications",
        to: "/PlainDutchWeavePage#specifications",
      },
      specs: [
        { label: "Plage de Maillage", value: "8×85 – 80×400 (jusqu'à 165×1200)" },
        { label: "Seuil Nominal", value: "~20 – 310 µm nominal" },
        { label: "Armure", value: "Tissage reps uni 1/1" },
        { label: "Alliages Standard", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Géométrie Triangulaire Micrométrique",
      heading: "Rétention Fiable et Dégagement Facilité du Gâteau",
      paragraphs: [
        "Dans le tissage reps uni (Plain Dutch Weave), les fils de chaîne sont maintenus tendus et rectilignes, tandis que les fils fins de trame sont serrés les uns contre les autres selon une armure unie un sur / un sous.",
        "Le contact continu des fils de trame interdit tout passage de lumière direct. Les fluides traversent obligatoirement des pores triangulaires micrométriques formés à la jonction tangentielle des fils.",
        "Cette structure procure une surface dense qui prévient l'encrassement interne et accélère le décolmatage lors des rétro-lavages. MutualFilter approvisionne rouleaux, bandes refendues et disques prêts à l'emploi.",
      ],
      keyPoints: [
        "Fils de chaîne robustes associés à une trame fine serrée formant des pores triangulaires réguliers pour une rétention prévisible et un démoulage aisé du gâteau.",
        "Absence totale d'ouvertures directes pour éviter la migration de particules sous pression cyclique.",
        "Plage standard de 8×85 à 80×400, variantes grand débit disponibles jusqu'à 165×1200.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Vue rapprochée toile reps uni",
      },
    },
    specTable: {
      heading: "Spécifications Standards Reps Uni",
      intro: "Données de fabrication pour toiles métalliques reps uni conformes aux normes ASTM E2814 et ISO 9044.",
      columns: [
        { key: "mesh", label: "Maillage (Chaîne × Trame)", align: "left" },
        { key: "warpWire", label: "Fil Chaîne (mm)", align: "right" },
        { key: "weftWire", label: "Fil Trame (mm)", align: "right" },
        { key: "retention", label: "Seuil Nominal (µm)", align: "right" },
        { key: "standard", label: "Norme", align: "right" },
      ],
      rows: [
        {
          id: "row-pd-1",
          mesh: "12 × 64",
          warpWire: "0.58",
          weftWire: "0.40",
          retention: "280 – 310",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-2",
          mesh: "24 × 110",
          warpWire: "0.35",
          weftWire: "0.25",
          retention: "115 – 125",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-3",
          mesh: "30 × 150",
          warpWire: "0.25",
          weftWire: "0.18",
          retention: "85 – 95",
          standard: "ISO 9044",
        },
        {
          id: "row-pd-4",
          mesh: "50 × 250",
          warpWire: "0.14",
          weftWire: "0.11",
          retention: "50 – 60",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-5",
          mesh: "80 × 400",
          warpWire: "0.13",
          weftWire: "0.07",
          retention: "25 – 35",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-6",
          mesh: "165 × 1200",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "20 – 25",
          standard: "ASTM E2814",
        },
      ],
      caption: "Seuils nominaux basés sur les tests de point de bulle selon ISO 9044.",
    },
    features: {
      heading: "Avantages Opérationnels",
      intro: "Points forts de l'armure reps uni pour la séparation industrielle.",
      items: [
        {
          title: "Robustesse Longitudinale",
          text: "Les fils de chaîne épais soutiennent les efforts mécaniques et limitent les déformations.",
        },
        {
          title: "Filtration en Surface",
          text: "Les matières solides sont retenues à l'extérieur sans s'incruster profondément dans le tissu.",
        },
        {
          title: "Stabilité Mécanique",
          text: "Le serrage intense des fils de trame empêche le déplacement des mailles sous vibrations.",
        },
        {
          title: "Nettoyage Chimique",
          text: "Nuances AISI 304, 304L, 316 et 316L adaptées aux cycles de nettoyage CIP en milieu agressif.",
        },
      ],
    },
    related: {
      heading: "Autres Tissages de Toile Filtrante",
      intro: "Comparez le reps uni avec les autres armures de la gamme reps.",
      cards: [
        {
          title: "Twill Dutch Weave",
          text: "L'armure sergée permet de doubler la densité de trame sans passage direct de lumière, assurant une filtration fine dès 1 micron sous forte pression différentielle.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Toile Twill Dutch",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Voir Twill Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Fils de chaîne denses combinés à des trames plus épaisses pour une résistance longitudinale élevée sur bandes de changeurs continus et filtration de polymères.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Toile Reverse Dutch",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Voir Reverse Dutch",
        },
        {
          title: "Vue d'ensemble Toiles Filtrantes",
          text: "Présentation globale des armures reps, des critères de sélection et des fabrications sur mesure.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Vue d'ensemble toiles reps",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Page Parent",
        },
      ],
    },
    cta: {
      id: "plain-dutch-cta",
      eyebrow: "Toile Reps sur Mesure",
      heading: "Consultez MutualFilter pour Vos Toiles Reps Uni",
      lead: "Bénéficiez de conseils techniques pour dimensionner vos toiles et commander rouleaux ou disques découpés.",
      secondaryLabel: "Voir Toiles Filtrantes",
      secondaryTo: "/FilterClothPage",
      note: "Certificats matière disponibles lorsque spécifiés dans la commande.",
    },
  },
  pt: {
    breadcrumbKey: "PlainDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tecido Filtrante Holandês",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Tela em Trama Holandesa Lisa",
      lead: "Fabricada com fios de urdume espessos e espaçados combinados com fios de trama finos e compactados, a trama holandesa lisa produz microporos triangulares regulares para retenção confiável de 20 µm a 310 µm com excelente descarte de torta.",
      secondaryCta: {
        label: "Ver Tecidos Filtrantes",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Tela em trama holandesa lisa em aço inoxidável",
      },
      cardNote: "Meio filtrante liso 1/1",
      cardLink: {
        label: "Especificações",
        to: "/PlainDutchWeavePage#specifications",
      },
      specs: [
        { label: "Faixa de Malha", value: "8×85 – 80×400 (até 165×1200)" },
        { label: "Retenção Nominal", value: "~20 – 310 µm nominal" },
        { label: "Estrutura", value: "Entrelaçamento holandês liso 1/1" },
        { label: "Ligas Padrão", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Geometria Triangular Micrométrica",
      heading: "Retenção Previsível e Remoção Rápida de Resíduos",
      paragraphs: [
        "Na trama holandesa lisa (Plain Dutch Weave), os fios de urdume permanecem retos sob alta tensão, enquanto os fios finos de trama são compactados com firmeza no clássico movimento de entrelaçamento 1 por cima / 1 por baixo.",
        "Como os fios de trama se tocam continuamente ao longo do tecido, a luz direta é inteiramente bloqueada. O fluido a ser filtrado percorre canais triangulares microscópicos definidos pelo contato tangencial dos fios.",
        "Essa geometria resulta em uma superfície densa que não retém sólidos em profundidade, agilizando a limpeza durante retrolavagens. A MutualFilter fornece bobinas, fitas sob medida e discos cortados a laser.",
      ],
      keyPoints: [
        "Fios de urdume grossos combinados com trama fina compactada produzem geometria uniforme de poros triangulares para retenção previsível e fácil descarte de torta.",
        "Bloqueio total de linha reta para evitar passagem de partículas sob variações cíclicas de pressão.",
        "Faixa padrão de malha de 8×85 a 80×400, com opções de alta vazão de até 165×1200.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Superfície da tela em trama holandesa lisa",
      },
    },
    specTable: {
      heading: "Especificações Padrão de Trama Holandesa Lisa",
      intro: "Dados de fabricação para telas holandesas lisas em aço inoxidável em conformidade com ASTM E2814 e ISO 9044.",
      columns: [
        { key: "mesh", label: "Malha (Urdume × Trama)", align: "left" },
        { key: "warpWire", label: "Fio Urdume (mm)", align: "right" },
        { key: "weftWire", label: "Fio Trama (mm)", align: "right" },
        { key: "retention", label: "Retenção Nominal (µm)", align: "right" },
        { key: "standard", label: "Norma", align: "right" },
      ],
      rows: [
        {
          id: "row-pd-1",
          mesh: "12 × 64",
          warpWire: "0.58",
          weftWire: "0.40",
          retention: "280 – 310",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-2",
          mesh: "24 × 110",
          warpWire: "0.35",
          weftWire: "0.25",
          retention: "115 – 125",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-3",
          mesh: "30 × 150",
          warpWire: "0.25",
          weftWire: "0.18",
          retention: "85 – 95",
          standard: "ISO 9044",
        },
        {
          id: "row-pd-4",
          mesh: "50 × 250",
          warpWire: "0.14",
          weftWire: "0.11",
          retention: "50 – 60",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-5",
          mesh: "80 × 400",
          warpWire: "0.13",
          weftWire: "0.07",
          retention: "25 – 35",
          standard: "ASTM E2814",
        },
        {
          id: "row-pd-6",
          mesh: "165 × 1200",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "20 – 25",
          standard: "ASTM E2814",
        },
      ],
      caption: "Classificação nominal de retenção conforme ensaios de ponto de bolha baseados na ISO 9044.",
    },
    features: {
      heading: "Vantagens Técnicas e Operacionais",
      intro: "Benefícios mecânicos da trama holandesa lisa em separações industriais contínuas.",
      items: [
        {
          title: "Base Longitudinal Robusta",
          text: "Fios de urdume fortes que absorvem os esforços mecânicos da vazão sob pressão.",
        },
        {
          title: "Filtragem Superficial",
          text: "Os particulados ficam concentrados na face do tecido, agilizando os procedimentos de retrolavagem.",
        },
        {
          title: "Fixação e Estabilidade",
          text: "A compactação dos fios de trama trava a posição da malha contra vibrações de linha.",
        },
        {
          title: "Resistência Química",
          text: "Aços inoxidáveis AISI 304, 304L, 316 e 316L adequados para rotinas CIP de higienização.",
        },
      ],
    },
    related: {
      heading: "Outras Tramas Filtrantes",
      intro: "Compare a trama holandesa lisa com as demais construções da linha.",
      cards: [
        {
          title: "Twill Dutch Weave",
          text: "A urdidura em sarja dobra a densidade da trama sem linha de visão direta, garantindo microrretenção a partir de 1 mícron sob pressões diferenciais severas.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Tecido Twill Dutch Weave",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Ver Twill Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Urdumes densos combinados com arames de trama mais grossos proporcionam extrema resistência à tração para fitas contínuas e filtração de polímeros sob alta pressão.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Tecido Reverse Dutch Weave",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Ver Reverse Dutch",
        },
        {
          title: "Visão Geral de Tecidos Filtrantes",
          text: "Panorama geral das tramas holandesas, critérios de seleção de ligas e configurações especiais.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Tecidos filtrantes visão geral",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Página Principal",
        },
      ],
    },
    cta: {
      id: "plain-dutch-cta",
      eyebrow: "Telas Industriais Sob Medida",
      heading: "Consulte a MutualFilter para Trama Holandesa Lisa",
      lead: "Fornecemos bobinas mestre, fitas cortadas e componentes conformados sob medida.",
      secondaryLabel: "Ver Tecidos Filtrantes",
      secondaryTo: "/FilterClothPage",
      note: "Relatórios de ensaio de material disponíveis mediante solicitação no pedido.",
    },
  },
};
